<template>
  <div>
    <v-form v-model="isFormValid">
      <div class="mb-8">
        <h1>{{ $t('children.createChildAccount') }}</h1>
      </div>

      <div class="my-8">
        <h4>{{ $t('children.childcareType') }}*</h4>
        <v-select
          :rules="[required]"
          variant="outlined"
          density="compact"
          hide-details
          :items="childTypeOptions"
          item-title="label"
          item-value="value"
          v-model="childcareType"
        ></v-select>
      </div>

      <div class="my-8">
        <h4 class="mb-2">{{ $t('common.name') }}*</h4>
        <v-row>
          <v-col cols="6">
            <v-text-field
              variant="outlined"
              density="compact"
              hide-details
              :label="$t('common.lastName')"
              v-model="lastName"
              :rules="[required]"
            ></v-text-field>
          </v-col>
          <v-col cols="6">
            <v-text-field
              variant="outlined"
              density="compact"
              hide-details
              :label="$t('common.firstName')"
              v-model="firstName"
              :rules="[required]"
            ></v-text-field>
          </v-col>
        </v-row>
      </div>

      <div class="my-8">
        <h4 class="mb-2">{{ $t('children.nurseryCodeNumber') }}</h4>
        <v-text-field
          variant="outlined"
          density="compact"
          hide-details
          :label="$t('children.optionalCode1')"
          v-model="optionalChildCode1"
          class="mb-2"
        ></v-text-field>
        <v-text-field
          variant="outlined"
          density="compact"
          hide-details
          :label="$t('children.optionalCode2')"
          v-model="optionalChildCode2"
        ></v-text-field>
      </div>

      <div class="my-8">
        <h4 class="mb-2">{{ $t('children.dateOfBirth') }}*</h4>
        <date-picker
          v-model="dateOfBirth"
          :label="$t('children.dateOfBirth')"
          :rules="[required]"
        ></date-picker>
      </div>

      <div class="my-8">
        <h4 class="mb-2">{{ $t('common.gender') }}*</h4>
        <v-select
          :rules="[required]"
          variant="outlined"
          density="compact"
          hide-details
          :items="genderOptions"
          item-title="label"
          item-value="value"
          v-model="gender"
        ></v-select>
      </div>

      <div class="my-8">
        <h4 class="mb-2">{{ $t('children.admissionDate') }}*</h4>
        <date-picker
          v-model="admissionDate"
          :label="$t('children.admissionDate')"
          :rules="[required]"
        ></date-picker>
      </div>

      <div class="my-8">
        <h4 class="mb-2">{{ $t('children.graduationDate') }}</h4>
        <date-picker
          v-model="graduationDate"
          :label="$t('children.graduationDate')"
        ></date-picker>
      </div>

      <div class="my-8">
        <h4>{{ $t('children.extendedCarePattern') }}</h4>
        <v-select
          v-model="hoursPatternId"
          :items="patternList"
          item-title="patternTitle"
          item-value="patternId"
          variant="outlined"
          density="compact"
          hide-details=""
        ></v-select>
      </div>

      <!-- <div class="my-8">
      <v-checkbox
        v-model="moveOrLeave"
        label="転園・卒園"
      ></v-checkbox>

      <div v-if="moveOrLeave">
        <v-textarea
          variant="outlined"
          density="compact"
          hide-details
          full-width
          label="理由"
          v-model="moveOrLeaveReason"
        ></v-textarea>
      </div>
    </div> -->

      <div>
        <v-progress-linear
          v-if="isCreating"
          indeterminate
          color="primary"
          class="my-8"
        ></v-progress-linear>
      </div>

      <div class="d-flex align-center">
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          variant="flat"
          x-large
          @click="create"
          :disabled="isCreating || !isFormValid"
          >{{ $t('common.save') }}</v-btn
        >
        <v-spacer></v-spacer>
      </div>
    </v-form>
  </div>
</template>
<script lang="ts">
import { defineComponent } from "vue";

import { childAccountCreateModule } from "@/store/dataModules/childAccountCreateModule";
import LocalDataService from "@/service/LocalDataService";
import { classModule } from "@/store/dataModules/classModule";
import {
  NurseryClassResponse,
  NurseryHoursPatternResponse,
} from "chaild-api/lib";
import ValidationMixin from "@/mixins/ValidationMixin";
import { nurseryHoursModule } from "@/store/dataModules/nurseryHoursModule";
import DatePicker from "@/components/atoms/DatePicker.vue";

interface DataType {
  childcareType: string | null;
  firstName: string | null;
  firstNameKana: string | null;
  lastName: string | null;
  lastNameKana: string | null;
  gender: string | null;
  dateOfBirth: string | null;
  optionalChildCode1: string | null;
  optionalChildCode2: string | null;
  admissionDate: string | null;
  graduationDate: string | null;
  moveOrLeave: boolean | null;
  reason: string | null;
  notaBene: string | null;
  hoursPatternId: string | null;

  isCreating: boolean;
  isFormValid: boolean;
}

export default defineComponent({
  mixins: [ValidationMixin],

  components: {
    DatePicker,
  },

  computed: {
    classes(): NurseryClassResponse[] {
      return classModule.classes;
    },
    classOptions(): any[] {
      return this.classes.map((c) => {
        return {
          classId: c.classId,
          className: `SY${c.year} ${c.className}`,
        };
      });
    },
    childTypeOptions() {
      return [
        { value: "normal", label: this.$t('children.regularChildcare') },
        { value: "temporary", label: this.$t('children.temporaryChildcare') },
      ];
    },
    genderOptions() {
      return [
        { value: "male", label: this.$t('common.male') },
        { value: "female", label: this.$t('common.female') },
      ];
    },
    patternList(): NurseryHoursPatternResponse[] {
      return nurseryHoursModule.patternList.map((pattern) => ({
        ...pattern,
        patternTitle:
          pattern.patternTitle === '延長パターン'
            ? (this.$t('children.extendedPatternDefault') as string)
            : pattern.patternTitle,
      }));
    },
  },

  data: (): DataType => {
    return {
      childcareType: null,
      firstName: null,
      firstNameKana: 'ア',
      lastName: null,
      lastNameKana: 'ア',
      gender: null,
      dateOfBirth: null,
      optionalChildCode1: null,
      optionalChildCode2: null,
      admissionDate: null,
      graduationDate: null,
      moveOrLeave: null,
      reason: null,
      notaBene: null,
      hoursPatternId: null,

      isCreating: false,
      isFormValid: false,
    };
  },

  methods: {
    async create() {
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        this.isCreating = true;

        try {
          const created = await childAccountCreateModule
            .createChild({
              nurseryId,
              childcareType: this.childcareType || undefined,
              firstName: this.firstName?.trim() || undefined,
              firstNameKana: this.firstNameKana?.trim() || undefined,
              lastName: this.lastName?.trim() || undefined,
              lastNameKana: this.lastNameKana?.trim() || undefined,
              gender: this.gender || undefined,
              dateOfBirth: this.dateOfBirth || undefined,
              optionalChildCode1: this.optionalChildCode1 || undefined,
              optionalChildCode2: this.optionalChildCode2 || undefined,
              admissionDate: this.admissionDate || undefined,
              graduationDate: this.graduationDate || undefined,
              moveOrLeave: this.moveOrLeave || false,
              reason: this.reason || undefined,
              notaBene: this.notaBene || undefined,
              hoursPatternId: this.hoursPatternId || undefined,
              allergicFoodIds: [],
            })
            .catch((e) => {
              console.warn(e);
            });
          if (created) {
            this.$router.push(`/children`);
          }
        } catch {
          this.isCreating = false;
        }

        this.isCreating = false;
      }
    },
  },

  async mounted() {
    const nurseryId = LocalDataService.getNurseryId();
    if (nurseryId) {
      await classModule.listClass({
        nurseryId: nurseryId,
      });
      await nurseryHoursModule.listPattern({
        nurseryId,
      });
    }
  },
});
</script>
